// ANA-R1-05 (class-flip / stale-cache fix) — invalidate the AI-finding +
// facet caches whenever a dataset is (re)opened.
//
// The walkthrough's "class flip" (a finding rendering as one AI class, then a
// different one after reopen — [00:20:15]) is a benign STALE-CACHE artifact,
// not corrupt data. A mid-session AI-findings re-import (delete-scope,
// same-model) SOFT-DELETES the old findings and RE-INSERTS replacements under
// NEW finding ids. The analysis surface, however, may still be holding the
// pre-import finding rows (and their class labels / bbox geometry) in the
// TanStack Query cache from before the import ran. Re-opening the dataset then
// reconciles selection / overlays against ids that no longer exist, so a box
// appears to flip class.
//
// WHY invalidate on the LYSE side (dataset-open) rather than from the import
// wizard's success path:
//
//   * The import wizard lives in `apps/web` (`features/ai-findings-import`),
//     the analysis surface in `apps/lyse`. apps/web is the umbrella SHELL that
//     MOUNTS the lyse module; it depends on lyse only through its public
//     `src/index.ts`, never on lyse-internal query-key factories. Reaching
//     into `findingKeys` / `analysisKeys` from the wizard would invert that
//     shell→module dependency and couple the two apps' cache internals.
//   * The two apps DO share one QueryClient (a single `createQueryClient()` in
//     `apps/web/src/app/providers.tsx`, handed to every mounted module via
//     context), so a wizard-side invalidation would technically reach the lyse
//     caches — but the import can target a DA that is not currently open (or
//     not open yet), and the surface re-mounts on open regardless. Invalidating
//     at dataset-open is the mechanism that ACTUALLY fires for the reported
//     reopen flow, with no cross-app coupling.
//
// Effect: on mount and whenever the active DA id changes, mark the finding list
// queries (all `findingKeys`) and the analysis data-point + facet queries
// (`analysisKeys.dataPoints(id)`, which covers the data-points list AND the
// ai-class-facets used by the AI Filter Module) as stale. Active observers
// refetch immediately, so a reopened dataset always shows the post-import
// finding ids + class labels.
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { findingKeys } from "../findings/queries.js";
import { analysisKeys } from "./queries.js";

export function useInvalidateFindingsOnOpen(dataAcquisitionId: string): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (dataAcquisitionId.length === 0) return;
    // All AI-finding lists for any DA (the key family is keyed by inspection id
    // one level down, so `findingKeys.all` is the narrowest key that covers a
    // freshly (re)opened DA's findings without enumerating param permutations).
    void qc.invalidateQueries({ queryKey: findingKeys.all });
    // The data-points list + every facet family (incl. ai-class-facets) for
    // THIS DA, so the AI Filter Module's class list / counts also refresh.
    void qc.invalidateQueries({
      queryKey: analysisKeys.dataPoints(dataAcquisitionId),
    });
  }, [qc, dataAcquisitionId]);
}
